import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Heart, MessageCircle, Trash2, Send } from "lucide-react";
import { Link } from "react-router-dom";

function PostCard({ p, onLike, onDelete, onComment }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const { user } = useAuth();

  const load = async () => {
    const { data } = await api.get(`/posts/${p.id}/comments`);
    setComments(data);
  };
  const toggle = async () => {
    if (!open) await load();
    setOpen(!open);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const { data } = await api.post(`/posts/${p.id}/comments`, { content: text });
    setComments(c => [...c, data]);
    setText("");
    onComment?.(p.id);
  };

  return (
    <article className="card-flat p-6" data-testid={`post-${p.id}`}>
      <header className="flex items-center justify-between">
        <Link to={`/u/${p.author?.id}`} className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-full bg-[#002855] text-[#C5A059] font-display flex items-center justify-center">{p.author?.name?.[0] || "?"}</div>
          <div>
            <div className="text-sm font-semibold text-[#0A192F] group-hover:text-[#002855]">{p.author?.name}</div>
            <div className="text-xs text-slate-500">{p.author?.agency} · Cohort {p.author?.cohort_year || "—"}</div>
          </div>
        </Link>
        <time className="text-xs text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}</time>
      </header>
      <p className="mt-4 text-[15px] leading-relaxed text-slate-800 whitespace-pre-line">{p.content}</p>
      {p.image_url && <img src={p.image_url} alt="" className="mt-4 rounded-md w-full max-h-96 object-cover" />}
      <div className="mt-5 flex items-center gap-2 border-t pt-4">
        <button onClick={() => onLike(p.id)} className={`inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-md border ${p.liked_by_me ? "border-[#9B111E] text-[#9B111E] bg-[#9B111E]/5" : "border-slate-200 text-slate-700 hover:border-[#002855]"}`} data-testid={`like-btn-${p.id}`}>
          <Heart className={`w-4 h-4 ${p.liked_by_me ? "fill-current" : ""}`} /> {p.likes_count}
        </button>
        <button onClick={toggle} className="inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-md border border-slate-200 text-slate-700 hover:border-[#002855]" data-testid={`comments-btn-${p.id}`}>
          <MessageCircle className="w-4 h-4" /> {p.comments_count}
        </button>
        {user && p.author?.id === user.id && (
          <button onClick={() => onDelete(p.id)} className="ml-auto inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-md text-slate-500 hover:text-[#9B111E]" data-testid={`delete-btn-${p.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && (
        <div className="mt-4 space-y-4">
          {comments.map(c => (
            <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <div className="w-8 h-8 rounded-full bg-slate-200 text-[#0A192F] text-xs flex items-center justify-center font-semibold">{c.author?.name?.[0]}</div>
              <div className="flex-1 bg-[#FDFBF7] border border-slate-200 rounded-md p-3">
                <div className="text-xs font-semibold text-[#0A192F]">{c.author?.name}</div>
                <div className="text-sm text-slate-700 mt-1">{c.content}</div>
              </div>
            </div>
          ))}
          <form onSubmit={submit} className="flex gap-2">
            <input value={text} onChange={e => setText(e.target.value)} placeholder="Add a comment…" className="flex-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002855]/20 focus:border-[#002855]" data-testid={`comment-input-${p.id}`} />
            <Button type="submit" className="bg-[#002855] hover:bg-[#0A192F] text-white h-10 rounded-md px-4" data-testid={`comment-submit-${p.id}`}>
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </article>
  );
}

export default function Feed({ groupId = null }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/posts", { params: groupId ? { group_id: groupId } : {} });
    setPosts(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [groupId]);

  const create = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    const { data } = await api.post("/posts", { content, group_id: groupId });
    setPosts(p => [data, ...p]);
    setContent("");
    setBusy(false);
  };

  const like = async (id) => {
    const { data } = await api.post(`/posts/${id}/like`);
    setPosts(p => p.map(x => x.id === id ? { ...x, liked_by_me: data.liked, likes_count: data.likes_count } : x));
  };

  const del = async (id) => {
    await api.delete(`/posts/${id}`);
    setPosts(p => p.filter(x => x.id !== id));
  };

  const onComment = (id) => setPosts(p => p.map(x => x.id === id ? { ...x, comments_count: x.comments_count + 1 } : x));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="feed-page">
      <div className="mb-8">
        <div className="label-eyebrow">{groupId ? "Group discussion" : "Members feed"}</div>
        <h1 className="mt-2 font-display text-4xl text-[#0A192F]">{groupId ? "Group" : "The Commons"}</h1>
      </div>

      <form onSubmit={create} className="card-flat p-5 mb-8" data-testid="post-composer">
        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-full bg-[#002855] text-[#C5A059] font-display flex items-center justify-center">{user?.name?.[0]}</div>
          <div className="flex-1">
            <Textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Share a story, an update, or a question with your fellow PMFs…" className="min-h-[88px] resize-none border-slate-200 focus-visible:ring-[#002855]/20" data-testid="post-content-input" />
            <div className="mt-3 flex justify-end">
              <Button type="submit" disabled={busy || !content.trim()} className="bg-[#002855] hover:bg-[#0A192F] text-white rounded-md" data-testid="post-submit-button">{busy ? "Posting…" : "Post"}</Button>
            </div>
          </div>
        </div>
      </form>

      <div className="space-y-5">
        {posts.length === 0 && <div className="text-center py-16 text-slate-500">Be the first to post.</div>}
        {posts.map(p => <PostCard key={p.id} p={p} onLike={like} onDelete={del} onComment={onComment} />)}
      </div>
    </div>
  );
}
